import { store } from "../store";
import DataStructure, { sanitize } from "./DataStructure";
import Address from "./Address";
import { deepFreeze, timestampToDate } from "../util/misc";
import {
  compact,
  every,
  flatten,
  get,
  isArray,
  isEmpty,
  mergeWith,
  omit,
  sortBy,
  uniq,
} from "lodash";
import { diff } from "deep-object-diff";
import { SALE_STATUS_TYPES } from "./SaleStatus.js";
import COLORS from "../colors.js";

// The mailbox project email syncs from, per Firebase project — the same
// values as PROJECT_MAILBOX in functions/.env.<projectId> (a spec pins the two
// together). Mail sent to its plus-address `<name>+<projectID>@<domain>`
// files itself under that project's Correspondence tab. Prod and staging have
// separate mailboxes because the sync marks what it imports as read.
export const PROJECT_MAILBOXES = {
  "northern-approach": "projects@nwdohio.com",
  "northern-approach-staging": "projects-staging@nwdohio.com",
};
deepFreeze(PROJECT_MAILBOXES);

export function projectMailbox(firebaseProjectId = process.env.projectId) {
  return PROJECT_MAILBOXES[firebaseProjectId] || null;
}

export const PROJECT_FIELDS = {
  name: "",
  address: null,
  assignedTo: null,
  teamID: null,
  type: "",
  soldDate: null,
  soldBy: null,
  soldTo: null,
  messageCount: 0,
  sales: {},
  archived: false,

  // Service tracking
  servicePhase: "none", // none, active, punchout_scheduled, punchout_complete, completed

  // Meta info
  created: null,
  createdBy: null,
  modified: null,
  modifiedBy: null,
};
deepFreeze(PROJECT_FIELDS);

export const SEARCHABLE_FIELDS = [
  "id",
  "saleIDs",
  "data.name",
  "soldBy.name",
  "assignedTo.name",
  "data.address.street",
  "data.address.lot",
  "data.address.development",
  "data.address.city",
  "data.address.state",
  "data.address.zip",
  "soldTo.contact.name",
  "data.soldTo.street",
  "data.soldTo.lot",
  "data.soldTo.development",
  "data.soldTo.city",
  "data.soldTo.state",
  "data.soldTo.zip",
  "brands",
  "series",
];
deepFreeze(SEARCHABLE_FIELDS);

export const PROJECT_TYPES = {
  residential: {
    label: "Residential",
    icon: "new_home",
  },
  commercial: {
    label: "Commercial",
    icon: "office",
  },
};
deepFreeze(PROJECT_TYPES);

export const MAP_STATUS_TYPES = {
  active: {
    label: "Active",
    color: "warning",
  },
  future: {
    label: "Future",
    color: "info",
  },
  past: {
    label: "Past",
    color: "positive",
  },
};
deepFreeze(MAP_STATUS_TYPES);

// Service phase tracks where a project is in the post-installation lifecycle
export const SERVICE_PHASES = {
  none: {
    order: 0,
    label: "No service",
    icon: "check",
    color: "grey",
  },
  active: {
    order: 1,
    label: "Active service",
    icon: "service",
    color: "warning",
  },
  punchout_scheduled: {
    order: 2,
    label: "Punchout scheduled",
    icon: "calendar",
    color: "info",
  },
  punchout_complete: {
    order: 3,
    label: "Punchout complete",
    icon: "success",
    color: "positive",
  },
  completed: {
    order: 4,
    label: "Service complete",
    icon: "done_all",
    color: "positive",
  },
};
deepFreeze(SERVICE_PHASES);

export default class Project extends DataStructure {
  constructor(id, data = {}) {
    data = sanitize(data, PROJECT_FIELDS);

    // Convert to Date
    data.created = timestampToDate(data.created);
    data.modified = timestampToDate(data.modified);
    data.soldDate = timestampToDate(data.soldDate);

    super(data);

    this.id = id || null;
    this.permissions = Object.freeze({
      create: Project.canBeCreatedBy,
      edit: this.canBeEditedBy,
      delete: this.canBeDeletedBy,
    });
  }

  // Permissions

  static canBeCreatedBy(user) {
    return user.isEmployee;
  }

  canBeEditedBy(user) {
    return user.isEmployee;
  }

  canBeDeletedBy(user) {
    return false;
  }

  // Null on a build with no project mailbox (an unknown Firebase project).
  get emailAddress() {
    const mailbox = projectMailbox();
    if (!mailbox) return null;
    const [name, domain] = mailbox.split("@");
    return `${name}+${this.id}@${domain}`;
  }

  // Database

  get path() {
    return Project.getPath(this.id);
  }

  static getPath(id) {
    let path = `projects`;
    if (id) {
      path += `/${id}`;
    }
    return path;
  }

  static toFirestore(project) {
    return project.data;
  }

  static fromFirestore(snapshot, options) {
    const data = snapshot.data(options);
    return new Project(snapshot.id, data);
  }

  // Editing

  get rules() {
    return {
      address: [() => this.address.isValid || "required"],
      assignedTo: [(a) => Boolean(a) || "required"],
      type: [(a) => Boolean(a) || "required"],
    };
  }

  get isValid() {
    return every(this.rules, (rules, key) =>
      rules.every((rule) => rule(get(this.data, key)) === true)
    );
  }

  get isEmpty() {
    return !this.id && isEmpty(diff(this.data, PROJECT_FIELDS));
  }

  isEqual(project) {
    return (
      this.id === project.id &&
      isEmpty(omit(diff(this.data, project.data), "modified"))
    );
  }

  // Formatting

  get sales() {
    return sortBy(
      this.saleIDs.map((id) => store.state.main.sales[id]),
      "created"
    );
  }

  get saleIDs() {
    return Object.keys(this.data.sales).map((id) => this.id + id);
  }

  get salesLeads() {
    return sortBy(
      Object.values(store.state.main.salesLeads).filter(
        (lead) => lead.projectID === this.id
      ),
      "created"
    );
  }

  get messages() {
    return sortBy(
      Object.values(store.state.main.messages).filter(
        (message) => message.projectID === this.id
      ),
      "created"
    );
  }

  get unseenMessageCount() {
    return this.messages.filter(
      (m) => !m.wasSeen && m.data.to.includes(store.state.main.user.id)
    ).length;
  }

  get type() {
    return PROJECT_TYPES[this.data.type];
  }

  get assignees() {
    const ids = [this.data.assignedTo];
    Object.values(this.data.sales).forEach((sale) => {
      ids.push(...sale.assignedTo);
    });
    return uniq(ids);
  }

  // Location value(s) used for location-based filtering. Prefers explicit sale
  // locations and otherwise derives from the assigned user(s).
  get locationValues() {
    const locations = [];
    this.sales.forEach((sale) => {
      if (sale && sale.data.location) {
        locations.push(sale.data.location);
      }
    });
    this.assignees.forEach((uid) => {
      const user = store.state.main.users[uid];
      if (user && Array.isArray(user.data.locations)) {
        locations.push(...user.data.locations);
      }
    });
    return uniq(compact(locations));
  }

  get brands() {
    return uniq(
      flatten(
        Object.values(this.data.sales).map((s) => Object.keys(s.brandSeries))
      )
    ).sort();
  }

  get series() {
    return uniq(
      flatten(
        Object.values(this.data.sales).map((s) =>
          flatten(Object.values(s.brandSeries))
        )
      )
    ).sort();
  }

  get brandSeries() {
    const brandSeries = mergeWith(
      {},
      ...Object.values(this.data.sales).map((s) => s.brandSeries),
      (objValue, srcValue) => {
        if (isArray(objValue)) {
          return uniq(objValue.concat(srcValue));
        }
      }
    );
    Object.values(brandSeries).forEach((series) => series.sort());
    return brandSeries;
  }

  get statusType() {
    const sales = Object.values(this.data.sales);
    if (!sales.length) {
      return null;
    }
    return sortBy(
      sales.map((s) => s.status).filter(Boolean),
      (s) => SALE_STATUS_TYPES[s]?.order ?? 999
    )[0];
  }

  get status() {
    const statusType = this.statusType;
    if (!statusType) {
      // No sales yet — e.g. a lead duplicated to a new project. Show a "New"
      // badge rather than a blank one.
      return {
        label: "New",
        icon: "new_home",
        order: -2,
        ...COLORS.info,
      };
    }
    const type = SALE_STATUS_TYPES[statusType];
    // Callers read .bg/.text straight off this (Projects list columns), so an
    // unrecognised status must still yield an object, never null.
    if (!type) return { icon: "", label: "", color: "", bg: "", text: "" };
    return { ...type, ...COLORS[type.color] };
  }

  get mapStatus() {
    const sales = Object.values(this.data.sales);
    if (
      this.data.archived ||
      sales.every((sale) => sale.punchOutComplete || !sale.requiresPunchOut)
    ) {
      return "past";
    } else if (sales.some((sale) => !sale.hasBegunDelivery)) {
      return "future";
    } else {
      return "active";
    }
  }

  get mapStatusType() {
    const type = MAP_STATUS_TYPES[this.mapStatus];
    return type ? { ...type, ...COLORS[type.color] } : null;
  }

  /**
   * True when no sale on this project still has a punchout coming — either it
   * has been completed or it was never required. Mirrors the denormalized
   * check in `mapStatus`; used to decide whether the "Punch list" urgency is
   * still offerable for a service issue.
   *
   * A project with no sales yet returns false: there is nothing to prove the
   * punchout is done, and wrongly hiding Punch list is worse than offering it.
   */
  get isPunchOutComplete() {
    const sales = Object.values(this.data.sales);
    if (!sales.length) return false;
    return sales.every(
      (sale) => sale.punchOutComplete || !sale.requiresPunchOut
    );
  }

  get servicePhase() {
    return this.data.servicePhase || "none";
  }

  get servicePhaseType() {
    const type = SERVICE_PHASES[this.servicePhase];
    return type ? { ...type, ...COLORS[type.color] } : null;
  }

  get address() {
    return new Address(this.data.address);
  }

  get soldTo() {
    return new Address(this.data.soldTo);
  }

  get assignedTo() {
    return this.data.assignedTo
      ? store.state.main.users[this.data.assignedTo] || null
      : null;
  }

  get team() {
    return this.data.teamID
      ? (store.state.main.salesTeams || {})[this.data.teamID] || null
      : null;
  }

  get soldBy() {
    return this.data.soldBy
      ? store.state.main.users[this.data.soldBy] || null
      : null;
  }

  get createdBy() {
    return this.data.createdBy
      ? store.state.main.users[this.data.createdBy] || null
      : null;
  }

  get modifiedBy() {
    return this.data.modifiedBy
      ? store.state.main.users[this.data.modifiedBy] || null
      : null;
  }

  toString() {
    return this.data.name || this.address.toString({ streetOnly: true });
  }
}
